/**
 * An explicit page break: "whatever follows starts a new sheet."
 *
 * WHY A DOCUMENT NEEDS ONE. Pagination here is computed -- the preview and the renderer both work
 * out where a page ends from the geometry -- and that is right for prose. It is wrong for the two
 * places a procedure insists: an appendix starts on its own page, and a form somebody signs is not
 * split across a fold. Those are decisions about the DOCUMENT, so they live in the document.
 *
 * A TOP-LEVEL ATOM. Not a mark, not a paragraph attribute: a break is a thing between two blocks,
 * with nothing inside it, and making it a property of the block after it would mean a break could
 * not exist at the end of a document or between two tables.
 *
 * DRAWN, NOT INVISIBLE. An editor where the break is invisible until you print is an editor where
 * people add three of them by accident. It renders as a labelled rule, and the pagination preview
 * treats it as the end of the sheet -- see pagination.ts -- so the page you see breaks where the
 * PDF breaks.
 */
import { Node, mergeAttributes } from "@tiptap/core";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        parkourPageBreak: {
            insertPageBreak: () => ReturnType;
        };
    }
}

export const PageBreak = Node.create({
    name: "pageBreak",
    group: "block",
    atom: true,
    selectable: true,
    draggable: false,

    parseHTML() {
        return [{ tag: "div[data-page-break]" }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, {
            "data-page-break": "true",
            class: "parkour-page-break",
            // THE LABEL IS CSS CONTENT, not a text node: a text node inside an atom is text the
            // editor would let somebody put a caret into and edit, and "Page break" is not writing.
        })];
    },

    addCommands() {
        return {
            insertPageBreak: () => ({ commands }: any) =>
                // A PARAGRAPH AFTER IT, ALWAYS. A break at the very end of a document with nothing
                // following is a break onto a blank page nobody asked for, and -- worse -- there is
                // nowhere to put the caret, so the next keystroke goes somewhere surprising.
                commands.insertContent([{ type: "pageBreak" }, { type: "paragraph" }]),
        };
    },
});

export default PageBreak;
